import React, { useState, useEffect } from 'react';
import {
  FiTrendingUp,
  FiTrendingDown,
  FiEye,
  FiUsers,
  FiHome,
  FiSearch,
  FiCalendar,
  FiFilter
} from 'react-icons/fi';
import { useAdminAuth } from '../../../hooks/admin/useAdminAuth';
import { API_URL } from '../../../services/api';

interface AnalyticsData {
  pageViews: {
    current: number;
    previous: number;
    change: number;
    data: Array<{ date: string; views: number }>;
  };
  searches: {
    current: number;
    previous: number;
    change: number;
    data: Array<{ date: string; searches: number }>;
  };
  companies: {
    total: number;
    active: number;
    new: number;
    change: number;
  };
  users: {
    total: number;
    active: number;
    new: number;
    change: number;
  };
  topCompanies: Array<{
    id: string;
    name: string;
    views: number;
    contacts: number;
    change: number;
  }>;
  topSearches: Array<{
    keyword: string;
    count: number;
    change: number;
  }>;
  provinceStats: Array<{
    province: string;
    companies: number;
    views: number;
    percentage: number;
  }>;
}

interface AnalyticsOverviewProps {
  dateRange?: string;
  onDateRangeChange?: (range: string) => void;
}

const AnalyticsOverview: React.FC<AnalyticsOverviewProps> = ({
  dateRange = '7d',
  onDateRangeChange
}) => {
  const { token } = useAdminAuth();
  const [loading, setLoading] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState<'views' | 'searches' | 'companies'>('views');
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Fallback mock data for display while loading or on error
  const mockData: AnalyticsData = {
    pageViews: {
      current: 24567,
      previous: 21890,
      change: 12.2,
      data: [
        { date: '2024-09-23', views: 3245 },
        { date: '2024-09-24', views: 3567 },
        { date: '2024-09-25', views: 3890 },
        { date: '2024-09-26', views: 4123 },
        { date: '2024-09-27', views: 3789 },
        { date: '2024-09-28', views: 3234 },
        { date: '2024-09-29', views: 2719 }
      ]
    },
    searches: {
      current: 8934,
      previous: 7654,
      change: 16.7,
      data: [
        { date: '2024-09-23', searches: 1245 },
        { date: '2024-09-24', searches: 1356 },
        { date: '2024-09-25', searches: 1487 },
        { date: '2024-09-26', searches: 1523 },
        { date: '2024-09-27', searches: 1389 },
        { date: '2024-09-28', searches: 1234 },
        { date: '2024-09-29', searches: 700 }
      ]
    },
    companies: {
      total: 1247,
      active: 1189,
      new: 12,
      change: 2.1
    },
    users: {
      total: 3892,
      active: 2156,
      new: 45,
      change: 8.3
    },
    topCompanies: [
      { id: '1', name: 'Bangkok Security Co., Ltd.', views: 1245, contacts: 89, change: 15.2 },
      { id: '2', name: 'Professional Guard Service', views: 987, contacts: 67, change: 8.7 },
      { id: '3', name: 'Elite Protection Ltd.', views: 756, contacts: 45, change: -2.3 },
      { id: '4', name: 'Thai Security Plus', views: 654, contacts: 34, change: 12.1 },
      { id: '5', name: 'Secure Force Co.', views: 543, contacts: 28, change: 5.8 }
    ],
    topSearches: [
      { keyword: 'รักษาความปลอดภัย กรุงเทพ', count: 456, change: 23.1 },
      { keyword: 'บริษัทรปภ', count: 342, change: 18.5 },
      { keyword: 'security company', count: 289, change: -5.2 },
      { keyword: 'ยาม 24 ชั่วโมง', count: 234, change: 12.7 },
      { keyword: 'บอดี้การ์ด', count: 198, change: 8.9 }
    ],
    provinceStats: [
      { province: 'กรุงเทพมหานคร', companies: 456, views: 12345, percentage: 36.5 },
      { province: 'นนทบุรี', companies: 123, views: 3456, percentage: 9.9 },
      { province: 'ปทุมธานี', companies: 98, views: 2789, percentage: 7.8 },
      { province: 'สมุทรปราการ', companies: 87, views: 2456, percentage: 7.0 },
      { province: 'ชลบุรี', companies: 76, views: 2123, percentage: 6.1 }
    ]
  };

  // Fetch analytics data from API
  useEffect(() => {
    const fetchAnalytics = async () => {
      if (!token) return;

      setLoading(true);
      setError(null);

      try {
        

        // Fetch all analytics data in parallel
        const [overviewRes, topCompaniesRes, topSearchesRes, provincesRes] = await Promise.all([
          fetch(`${API_URL}/api/admin/analytics/overview?date_range=${dateRange}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          fetch(`${API_URL}/api/admin/analytics/top-companies?limit=5&date_range=${dateRange}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          fetch(`${API_URL}/api/admin/analytics/popular-searches?limit=5&date_range=${dateRange}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          fetch(`${API_URL}/api/admin/analytics/provinces?limit=5&date_range=${dateRange}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          })
        ]);

        if (!overviewRes.ok) throw new Error('Failed to fetch analytics overview');

        const overviewData = await overviewRes.json();
        const topCompaniesData = await topCompaniesRes.json();
        const topSearchesData = await topSearchesRes.json();
        const provincesData = await provincesRes.json();

        // Transform API data to match component structure
        setAnalyticsData({
          pageViews: {
            current: overviewData.data.visitors.count,
            previous: Math.floor(overviewData.data.visitors.count / (1 + overviewData.data.visitors.change_percent / 100)),
            change: overviewData.data.visitors.change_percent,
            data: overviewData.data.visitors.sparkline.map((views: number, index: number) => ({
              date: new Date(Date.now() - (6 - index) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
              views
            }))
          },
          searches: {
            current: overviewData.data.searches.count,
            previous: Math.floor(overviewData.data.searches.count / (1 + overviewData.data.searches.change_percent / 100)),
            change: overviewData.data.searches.change_percent,
            data: overviewData.data.searches.sparkline.map((searches: number, index: number) => ({
              date: new Date(Date.now() - (6 - index) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
              searches
            }))
          },
          companies: {
            total: overviewData.data.companies.total,
            active: overviewData.data.companies.active,
            new: overviewData.data.companies.new_count,
            change: overviewData.data.companies.change_percent
          },
          users: {
            total: overviewData.data.users.total,
            active: overviewData.data.users.active,
            new: overviewData.data.users.new_count,
            change: overviewData.data.users.change_percent
          },
          topCompanies: topCompaniesData.data.map((company: any) => ({
            id: company.id,
            name: company.name,
            views: company.views,
            contacts: company.contacts,
            change: company.change_percent
          })),
          topSearches: topSearchesData.data.map((search: any) => ({
            keyword: search.keyword,
            count: search.count,
            change: search.change_percent
          })),
          provinceStats: provincesData.data.map((province: any) => ({
            province: province.province,
            companies: province.companies,
            views: province.views,
            percentage: province.percentage
          }))
        });
      } catch (err) {
        console.error('Error fetching analytics:', err);
        setError(err instanceof Error ? err.message : 'Failed to load analytics');
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [token, dateRange]);

  const handleDateRangeChange = (range: string) => {
    onDateRangeChange?.(range);
    // Data will be refetched via useEffect when dateRange changes
  };

  const renderTrendIcon = (change: number) => {
    if (change > 0) {
      return <FiTrendingUp className="h-4 w-4 text-green-500" />;
    } else if (change < 0) {
      return <FiTrendingDown className="h-4 w-4 text-red-500" />;
    }
    return <div className="h-4 w-4" />;
  };

  const formatChange = (change: number) => {
    const sign = change > 0 ? '+' : '';
    const color = change > 0 ? 'text-green-600' : change < 0 ? 'text-red-600' : 'text-gray-600';
    return (
      <span className={`text-sm font-medium ${color}`}>
        {sign}{change.toFixed(1)}%
      </span>
    );
  };

  const renderSimpleChart = (data: Array<{ date: string; views?: number; searches?: number }>, type: 'views' | 'searches') => {
    const values = data.map(d => type === 'views' ? d.views || 0 : d.searches || 0);
    const max = Math.max(...values);
    const min = Math.min(...values);
    const range = max - min;

    return (
      <div className="flex items-end justify-between h-16 mt-4">
        {values.map((value, index) => {
          const height = range > 0 ? ((value - min) / range) * 100 : 50;
          return (
            <div
              key={index}
              className="bg-blue-500 rounded-t flex-1 mx-0.5 min-h-[4px]"
              style={{ height: `${Math.max(height, 10)}%` }}
              title={`${data[index].date}: ${value.toLocaleString()}`}
            />
          );
        })}
      </div>
    );
  };

  // Use real data if available, fallback to mock data
  const data = analyticsData || mockData;

  return (
    <div className="space-y-6">
      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          <p className="font-medium">เกิดข้อผิดพลาดในการโหลดข้อมูล</p>
          <p className="text-sm">{error}</p>
        </div>
      )}

      {/* Header with Date Range Selector */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex-1 min-w-0">
          <h2 className="text-lg md:text-xl font-semibold text-gray-900">ภาพรวมการใช้งาน</h2>
          <p className="text-xs md:text-sm text-gray-600 mt-1">
            สถิติและข้อมูลการใช้งานเว็บไซต์
            {!analyticsData && !error && !loading && <span className="text-amber-600"> (ข้อมูลตัวอย่าง)</span>}
          </p>
        </div>
        <div className="flex items-center space-x-2 md:space-x-3 flex-shrink-0">
          <FiCalendar className="h-4 w-4 text-gray-400 hidden sm:block" />
          <select
            value={dateRange}
            onChange={(e) => handleDateRangeChange(e.target.value)}
            className="block w-full sm:w-auto border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-xs md:text-sm px-3 py-2"
          >
            <option value="1d">วันนี้</option>
            <option value="7d">7 วันที่แล้ว</option>
            <option value="30d">30 วันที่แล้ว</option>
            <option value="90d">90 วันที่แล้ว</option>
            <option value="1y">1 ปีที่แล้ว</option>
          </select>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        {/* Page Views */}
        <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <FiEye className="h-6 w-6 md:h-8 md:w-8 text-blue-600" />
            {renderTrendIcon(data.pageViews.change)}
          </div>
          <div className="space-y-1">
            <p className="text-xl md:text-2xl font-bold text-gray-900">
              {data.pageViews.current.toLocaleString()}
            </p>
            <p className="text-xs md:text-sm text-gray-600">ผู้เข้าชม</p>
            <div className="flex items-center justify-between">
              {formatChange(data.pageViews.change)}
              <span className="text-xs text-gray-500">
                vs {dateRange === '7d' ? 'สัปดาห์ก่อน' : 'ช่วงก่อน'}
              </span>
            </div>
          </div>
          {renderSimpleChart(data.pageViews.data, 'views')}
        </div>

        {/* Searches */}
        <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <FiSearch className="h-6 w-6 md:h-8 md:w-8 text-green-600" />
            {renderTrendIcon(data.searches.change)}
          </div>
          <div className="space-y-1">
            <p className="text-xl md:text-2xl font-bold text-gray-900">
              {data.searches.current.toLocaleString()}
            </p>
            <p className="text-xs md:text-sm text-gray-600">การค้นหา</p>
            <div className="flex items-center justify-between">
              {formatChange(data.searches.change)}
              <span className="text-xs text-gray-500">
                vs {dateRange === '7d' ? 'สัปดาห์ก่อน' : 'ช่วงก่อน'}
              </span>
            </div>
          </div>
          {renderSimpleChart(data.searches.data, 'searches')}
        </div>

        {/* Companies */}
        <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <FiHome className="h-6 w-6 md:h-8 md:w-8 text-purple-600" />
            {renderTrendIcon(data.companies.change)}
          </div>
          <div className="space-y-1">
            <p className="text-xl md:text-2xl font-bold text-gray-900">
              {data.companies.total.toLocaleString()}
            </p>
            <p className="text-xs md:text-sm text-gray-600">บริษัททั้งหมด</p>
            <div className="flex items-center justify-between">
              {formatChange(data.companies.change)}
              <span className="text-xs text-gray-500">
                +{data.companies.new} ใหม่
              </span>
            </div>
          </div>
          <div className="mt-3 md:mt-4">
            <div className="flex justify-between text-xs text-gray-600">
              <span>ใช้งาน</span>
              <span>{data.companies.active}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
              <div
                className="bg-purple-600 h-2 rounded-full"
                style={{ width: `${(data.companies.active / data.companies.total) * 100}%` }}
              />
            </div>
          </div>
        </div>

        {/* Users */}
        <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <FiUsers className="h-6 w-6 md:h-8 md:w-8 text-yellow-600" />
            {renderTrendIcon(data.users.change)}
          </div>
          <div className="space-y-1">
            <p className="text-xl md:text-2xl font-bold text-gray-900">
              {data.users.total.toLocaleString()}
            </p>
            <p className="text-xs md:text-sm text-gray-600">ผู้ใช้งาน</p>
            <div className="flex items-center justify-between">
              {formatChange(data.users.change)}
              <span className="text-xs text-gray-500">
                +{data.users.new} ใหม่
              </span>
            </div>
          </div>
          <div className="mt-3 md:mt-4">
            <div className="flex justify-between text-xs text-gray-600">
              <span>ใช้งานอยู่</span>
              <span>{data.users.active}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
              <div
                className="bg-yellow-600 h-2 rounded-full"
                style={{ width: `${(data.users.active / data.users.total) * 100}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Companies */}
        <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-4">บริษัทยอดนิยม</h3>
          <div className="space-y-3 md:space-y-4">
            {data.topCompanies.map((company, index) => (
              <div key={company.id} className="flex items-center justify-between gap-2">
                <div className="flex items-center space-x-2 md:space-x-3 flex-1 min-w-0">
                  <span className="text-xs md:text-sm font-medium text-gray-500 w-4 flex-shrink-0">
                    {index + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs md:text-sm font-medium text-gray-900 truncate">
                      {company.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {company.contacts} ติดต่อ
                    </p>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-xs md:text-sm font-medium text-gray-900">
                    {company.views.toLocaleString()}
                  </p>
                  {formatChange(company.change)}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top Searches */}
        <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-4">คำค้นหายอดนิยม</h3>
          <div className="space-y-3 md:space-y-4">
            {data.topSearches.map((search, index) => (
              <div key={index} className="flex items-center justify-between gap-2">
                <div className="flex items-center space-x-2 md:space-x-3 flex-1 min-w-0">
                  <span className="text-xs md:text-sm font-medium text-gray-500 w-4 flex-shrink-0">
                    {index + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs md:text-sm font-medium text-gray-900 truncate">
                      {search.keyword}
                    </p>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-xs md:text-sm font-medium text-gray-900">
                    {search.count.toLocaleString()}
                  </p>
                  {formatChange(search.change)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Province Stats */}
      <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-4">สถิติตามจังหวัด</h3>
        <div className="space-y-3 md:space-y-4">
          {data.provinceStats.map((stat, index) => (
            <div key={index} className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs md:text-sm font-medium text-gray-900 truncate flex-1 pr-2">
                  {stat.province}
                </p>
                <span className="text-xs md:text-sm font-medium text-gray-900 flex-shrink-0">
                  {stat.percentage}%
                </span>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                <div className="w-full bg-gray-200 rounded-full h-2 flex-1">
                  <div
                    className="bg-blue-600 h-2 rounded-full"
                    style={{ width: `${stat.percentage}%` }}
                  />
                </div>
                <div className="flex items-center gap-3 text-xs text-gray-600 flex-shrink-0">
                  <span className="whitespace-nowrap">{stat.companies} บริษัท</span>
                  <span className="whitespace-nowrap">{stat.views.toLocaleString()} views</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {loading && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded-lg shadow-lg">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-sm text-gray-600 mt-2">กำลังโหลดข้อมูล...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsOverview;